import { createFileRoute } from "@tanstack/react-router";
import { Activity, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { useAllCellHealth } from "@/hooks/use-cell-health";
import { CellFailuresAlert } from "@/components/cells/cell-failures-alert";
import { healthUnknown, isUncheckedCell } from "@/lib/cells/cell-health";
import { CELLS, PRIMARY_CELL } from "@/lib/cells/cells";

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/health")({
  component: HealthPage,
});

function HealthPage() {
  const { cells, primary, failures, allHealthy, isLoading, primaryReadiness } =
    useAllCellHealth();
  const health = primary?.health;
  const readiness = primaryReadiness.data;
  const multiCell = CELLS.length > 1;
  // The summary cards describe the primary cell; say so once there are others.
  const primaryLabel = multiCell ? ` (${PRIMARY_CELL.id})` : "";

  // Only the primary cell holds the page; other cells' rows fill in as they answer.
  if (primary?.isLoading || primaryReadiness.isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="border-primary h-12 w-12 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "healthy":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "warning":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case "error":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "healthy":
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            Healthy
          </Badge>
        );
      case "warning":
        return (
          <Badge
            variant="outline"
            className="border-yellow-500 text-yellow-700"
          >
            Warning
          </Badge>
        );
      case "error":
        return <Badge variant="destructive">Error</Badge>;
      case "checking":
        return <Badge variant="secondary">Checking…</Badge>;
      case "unchecked":
        return <Badge variant="secondary">Not checked</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <div className="flex flex-row items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-[-1%]">
            Health Check
          </h2>
          <p className="text-muted-foreground">
            Monitor the health and performance of all system components
          </p>
        </div>
        {!multiCell || allHealthy ? (
          <Badge variant="default" className="bg-green-100 text-green-800">
            All Systems Operational
          </Badge>
        ) : isLoading ? (
          <Badge variant="secondary">Checking cells…</Badge>
        ) : (
          <Badge
            variant="outline"
            className="border-yellow-500 text-yellow-700"
          >
            Not every cell is healthy
          </Badge>
        )}
      </div>

      <CellFailuresAlert
        failures={multiCell ? failures : []}
        heading={healthUnknown}
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Service Status{primaryLabel}
            </CardTitle>
            <Activity className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {health?.status === "healthy" ? "Healthy" : "Unhealthy"}
            </div>
            <p className="text-muted-foreground text-xs">Core service status</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Readiness{primaryLabel}
            </CardTitle>
            <Activity className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {readiness?.status === "ready" ? "Ready" : "Not Ready"}
            </div>
            <p className="text-muted-foreground text-xs">
              Service readiness status
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Uptime{primaryLabel}
            </CardTitle>
            <Activity className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {health?.uptime ? `${Math.floor(health.uptime / 60)}m` : "N/A"}
            </div>
            <p className="text-muted-foreground text-xs">Service uptime</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Service Health</CardTitle>
          <CardDescription>
            Real-time status of all system components
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {cells.map((result) => {
              const { cell, health: cellHealth, isLoading } = result;
              const unchecked = isUncheckedCell(result);
              const status = isLoading
                ? "checking"
                : unchecked
                  ? "unchecked"
                  : cellHealth?.status === "healthy"
                    ? "healthy"
                    : "error";
              return (
                <div
                  key={cell.id}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(status)}
                    <div>
                      <h4 className="font-medium">
                        Tenants Service
                        {CELLS.length > 1 && ` (${cell.id}, ${cell.residency})`}
                      </h4>
                      <p className="text-muted-foreground text-sm">
                        Status:{" "}
                        {isLoading
                          ? "Checking…"
                          : unchecked
                            ? `Not checked (not signed in to ${cell.id})`
                            : (cellHealth?.status ?? "Unknown")}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(status)}
                </div>
              );
            })}

            {readiness?.checks &&
              Object.keys(readiness.checks).length > 0 &&
              Object.entries(readiness.checks).map(([checkName]) => (
                <div
                  key={checkName}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="flex items-center space-x-3">
                    {getStatusIcon("healthy")}
                    <div>
                      <h4 className="font-medium">{checkName}</h4>
                      <p className="text-muted-foreground text-sm">
                        Readiness check result
                      </p>
                    </div>
                  </div>
                  {getStatusBadge("healthy")}
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
